/**
 * Purpose: Searchable dropdown for long option lists (cities, societies, etc.).
 * Inputs: options, value, onChange, label, placeholder, disabled, loading, emptyOption.
 * Outputs: Combobox UI; onChange receives { target: { name, value } } like native select.
 */
import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import {
  filterOptionsByQuery,
  getSelectedOptionLabel,
  normalizeSelectOptions,
} from '../../utils/filterSelectOptions';

const DEFAULT_SEARCH_THRESHOLD = 8;

const SearchableSelect = ({
  id,
  name,
  label,
  value = '',
  onChange,
  options = [],
  placeholder = 'Select…',
  searchPlaceholder = 'Type to search…',
  disabled = false,
  loading = false,
  loadingLabel = 'Loading…',
  emptyOption = null,
  required = false,
  error = null,
  className = '',
  searchable,
  searchThreshold = DEFAULT_SEARCH_THRESHOLD,
  listMaxHeightClass = 'max-h-48',
  'aria-label': ariaLabel,
}) => {
  const reactId = useId();
  const controlId = id || `searchable-select-${reactId}`;
  const listboxId = `${controlId}-listbox`;
  const containerRef = useRef(null);
  const searchInputRef = useRef(null);

  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');

  const normalizedOptions = useMemo(() => normalizeSelectOptions(options), [options]);
  const isSearchable =
    searchable ?? normalizedOptions.length > searchThreshold;

  const filteredOptions = useMemo(() => {
    if (!isSearchable || !open) return normalizedOptions;
    return filterOptionsByQuery(normalizedOptions, query);
  }, [normalizedOptions, isSearchable, open, query]);

  const selectedLabel = useMemo(() => {
    if (emptyOption && String(value) === String(emptyOption.value ?? '')) {
      return emptyOption.label || placeholder;
    }
    return getSelectedOptionLabel(normalizedOptions, value) || placeholder;
  }, [normalizedOptions, value, emptyOption, placeholder]);

  const close = useCallback(() => {
    setOpen(false);
    setQuery('');
  }, []);

  const emitChange = useCallback(
    (nextValue) => {
      if (typeof onChange === 'function') {
        onChange({ target: { name: name || '', value: nextValue } });
      }
      close();
    },
    [onChange, name, close],
  );

  useEffect(() => {
    if (!open) return undefined;
    const handlePointerDown = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        close();
      }
    };
    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, [open, close]);

  useEffect(() => {
    if (open && isSearchable && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [open, isSearchable]);

  const handleToggle = () => {
    if (disabled || loading) return;
    setOpen((prev) => !prev);
    setQuery('');
  };

  const borderClass = error ? 'border-red-300' : 'border-gray-300';

  return (
    <div className={className}>
      {label ? (
        <label htmlFor={controlId} className="block text-sm font-medium text-gray-700 mb-1">
          {label}
          {required ? <span className="text-red-500"> *</span> : null}
        </label>
      ) : null}
      <div ref={containerRef} className="relative">
        <button
          type="button"
          id={controlId}
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-controls={listboxId}
          aria-label={ariaLabel || label || placeholder}
          disabled={disabled || loading}
          onClick={handleToggle}
          className={`w-full min-h-[44px] px-3 py-2 text-left text-sm border rounded-md bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:text-gray-500 ${borderClass} ${
            !value && emptyOption ? 'text-gray-500' : ''
          }`}
        >
          {loading ? loadingLabel : selectedLabel}
        </button>

        {open && (
          <div
            className={`absolute z-50 mt-1 w-full rounded-md border border-gray-300 bg-white shadow-lg overflow-hidden ${listMaxHeightClass} flex flex-col`}
          >
            {isSearchable && (
              <div className="p-2 border-b border-gray-200 shrink-0">
                <input
                  ref={searchInputRef}
                  type="search"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder={searchPlaceholder}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  autoComplete="off"
                  aria-label={searchPlaceholder}
                />
              </div>
            )}
            <ul
              id={listboxId}
              role="listbox"
              className="overflow-y-auto flex-1 py-1"
              aria-label={label || placeholder}
            >
              {emptyOption && (
                <li role="presentation">
                  <button
                    type="button"
                    role="option"
                    aria-selected={String(value) === String(emptyOption.value ?? '')}
                    onClick={() => emitChange(emptyOption.value ?? '')}
                    className="w-full px-3 py-2 text-left text-sm text-gray-600 hover:bg-gray-50 focus:bg-gray-50 focus:outline-none"
                  >
                    {emptyOption.label}
                  </button>
                </li>
              )}
              {filteredOptions.length === 0 && (
                <li className="px-3 py-2 text-sm text-gray-500 text-center">
                  {query.trim() ? 'No matches found.' : 'No options available.'}
                </li>
              )}
              {filteredOptions.map((opt) => (
                <li key={opt.value} role="presentation">
                  <button
                    type="button"
                    role="option"
                    aria-selected={String(value) === opt.value}
                    onClick={() => emitChange(opt.value)}
                    className={`w-full px-3 py-2 text-left text-sm hover:bg-blue-50 focus:bg-blue-50 focus:outline-none ${
                      String(value) === opt.value
                        ? 'bg-blue-50 text-blue-900 font-medium'
                        : 'text-gray-900'
                    }`}
                  >
                    {opt.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
      {error ? <p className="mt-1 text-sm text-red-600">{error}</p> : null}
    </div>
  );
};

export default SearchableSelect;
